import React, { useEffect, useRef, useState, useLayoutEffect } from 'react';
import * as d3 from 'd3';
import { Button, Switch, Typography, Box, FormControlLabel, Checkbox, IconButton, Popover } from '@mui/material';
import APIService from '../../api/apiService';
import RoutinesPopup from "../crops/Popup/RoutinesPopup"
import { hasPermissions } from '../../utility';
import { useMemo } from 'react';
import { cropPlanModelName } from '../utility';
import { useToken } from '../../utility';
import { is } from 'date-fns/locale';

const CommandCenterChart = ({
  data,
  statusChoices,
  currDarkThemeClass,
  selectedStates,
  setSelectedStates,
  // phaseDetails, 
  onSubphaseClick,
  drawerData,
  viewBy,
  setViewBy,
  selectedYear = 2024,
  onSwitchToCommandView,
  routine,
  validate,

  alert
}) => {
  const svgRef = useRef();
  const headerSvgRef = useRef();
  const containerRef = useRef();
  const headerContainerRef = useRef();
  const selectedCropYRef = useRef(null);
  const isDark = currDarkThemeClass === 'dark-theme';

  const theme = {
    bg:           isDark ? '#2E2F2D' : '#F5F5F3',
    headerBg:     isDark ? '#3A3B39' : '#E8E8E6',
    panelBg:      isDark ? '#141414' : '#FFFFFF',
    cardBg:       isDark ? '#1a1a1a' : '#F0F0EE',
    innerCardBg:  isDark ? '#1c1c1c' : '#E8E8E6',
    border:       isDark ? '#2a2a2a' : '#DDDDDD',
    divider:      isDark ? '#222'    : '#E0E0E0',
    text:         isDark ? '#FFFFFF' : '#1A1B19',
    textMuted:    isDark ? '#666666' : '#888888',
    textFaint:    isDark ? '#444444' : '#BBBBBB',
    svgText:      isDark ? 'white'   : '#1A1B19',
    svgBg:        isDark ? '#333433' : '#E5E5E3',
    barColor:     isDark ? '#414437' : '#6B6E5A',
    accent:       '#B1D23A',
    icon:         isDark ? 'white' : 'black',
    rect:         isDark ? '#414437' : 'white',
  };
  const { token, tokenDetails, permissions } = useToken();

  const [timeRange, setTimeRange] = useState('1Y');
  const range = ['3M', '6M', '1Y', '2Y', '3Y']
  const [dateDomain, setDateDomain] = useState(null);
  const [chartView, setChartView] = useState('chart');
  const [tempStates, setTempStates] = useState(Array.isArray(selectedStates) ? selectedStates : Object.keys(selectedStates || {}));
  const STATUS_COLORS = {
    "Planning": "#2196F3",          // Blue
    "Ready For Grow": "#4CAF50",    // Green
    "Active": "#FF9800",            // Orange
    "Complete": "#9C27B0",          // Purple
    "Destroyed": "#F44336",         // Red
    "Archived": "#607D8B"           // Grey-Blue
  };

  const getTaskCountsForCrop = (cropId) => {
    // Handles both: full list of tasks OR {crop_id, count}
    const getCount = (arr) => {
      if (!Array.isArray(arr)) return 0;
      if (arr.length === 0) return 0;

      // Case 1: cached format (contains count key)
      if ("count" in arr[0]) {
        return arr.find((r) => r.crop_id === cropId)?.count || 0;
      }

      // Case 2: uncached format (list of task objects)
      return arr.filter((r) => r.crop_id === cropId).length;
    };

    const routineCount = getCount(routine);
    const validateCount = getCount(validate);
    const alertCount = getCount(alert);

    return { routineCount, validateCount, alertCount };
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const [phaseData, setPhaseData] = useState(null);
  const [expandedCropId, setExpandedCropId] = useState(null);
  const [expandedPhaseId, setExpandedPhaseId] = useState(null);
  const [phaseCache, setPhaseCache] = useState({});
  const [expandedSubphaseId, setExpandedSubphaseId] = useState(null);
  const [selectedRoutine, setSelectedRoutine] = useState(null);
  const [assignees, setAssignees] = useState(null)
  const [users, setUsers] = useState(null)
  const [activeSubphase, setActiveSubphase] = useState(null);
  const [containerWidth, setContainerWidth] = useState(1200);

  const open = Boolean(anchorEl);
  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleTempChange = (status, checked) => {
    setTempStates(prev =>
      checked ? [...prev, status] : prev.filter(s => s !== status)
    );
  };

  const handlePhaseClick = (phaseId) => {
    setExpandedPhaseId(prev => prev === phaseId ? null : phaseId);
    setExpandedSubphaseId(null);
  };

  const isSubphaseComplete = (subphase) => {
    const routines = subphase.routines || [];
    if (!routines.length) return false;

    return routines.every(routine => {
      const routineTasks = routine.routine_tasks || [];
      if (!routineTasks.length) return false;

      return !routineTasks.some(
        task => task.status !== 'complete' && task.status !== 'metrc_validated'
      );
    });
  };
  const cropPlanPerms = useMemo(() => {
    return hasPermissions(
      [cropPlanModelName],
      { ...tokenDetails, permissions }
    )[cropPlanModelName] ?? {};
  }, [token]);

  const handleApply = () => {
    setSelectedStates(tempStates);
    handleClose();
  };

  const handleSelectAll = () => {
    setTempStates(Object.keys(statusChoices || {}));
  };

  const handleClearAll = () => {
    setTempStates([]);
  };

  const handleRectClick = async (cropId) => {
    const isSame = expandedCropId === cropId;

    setExpandedPhaseId(null);
    setPhaseData(null);

    if (isSame) {
      setExpandedCropId(null);
      return;
    }

    setExpandedCropId(cropId);

    if (phaseCache[cropId]) {
      setPhaseData(phaseCache[cropId]);
      return;
    }

    try {
      const res = await APIService.fetchInstance(
        `v2/cropplan/phases/?id=${cropId}`
      );

      const phases = res.data[0]?.phases || [];

      setPhaseData(phases);

      setPhaseCache(prev => ({
        ...prev,
        [cropId]: phases
      }));
    } catch (err) {
      console.error(err);
    }
  };

  const handleRoutineSave = (subphase_id, routine_id) => async (value) => {
    try {
      const entity = `v2/routine/${routine_id}/`;
      const res = await APIService.modifyInstance(entity, value);

      const updatedRoutine = res.data;

      setPhaseData(prev =>
        prev?.map(phase => ({
          ...phase,
          subphases: phase.subphases.map(sub =>
            sub.id === subphase_id
              ? {
                  ...sub,
                  routines: sub.routines.map(r =>
                    r.id === routine_id ? updatedRoutine : r
                  ),
                }
              : sub
          ),
        }))
      );

    } catch (err) {
      console.error("Save failed:", err);
    }

    setSelectedRoutine(null);
  };

  const getTaskCountsForStrain = (cropIds) => {
    if (!Array.isArray(cropIds) || cropIds.length === 0) {
      return { routineCount: 0, validateCount: 0, alertCount: 0 };
    }

    const getCount = (arr) => {
      if (!Array.isArray(arr)) return 0;
      if (arr.length === 0) return 0;

      if ("count" in arr[0]) {
        // cached format
        return arr
          .filter((r) => cropIds.includes(r.crop_id))
          .reduce((sum, r) => sum + (r.count || 0), 0);
      }

      // uncached format
      return arr.filter((r) => cropIds.includes(r.crop_id)).length;
    };

    return {
      routineCount: getCount(routine),
      validateCount: getCount(validate),
      alertCount: getCount(alert),
    };
  };


  const handleIconClick = (cropId, stateType) => {
    const searchQuery = `C${cropId}`;
    const params = {
      state: stateType,
      cropId: cropId,
      searchQuery: searchQuery
    };

    if (onSwitchToCommandView && typeof onSwitchToCommandView === 'function') {
      onSwitchToCommandView(params);
    } else {
      console.error("❌ handleIconClick - onSwitchToCommandView is not available:", {
        exists: !!onSwitchToCommandView,
        type: typeof onSwitchToCommandView
      });
    }
  };


  const processedCropData = React.useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }

    if (viewBy === 'crop') {
      return data.map((crop) => {
        let startDate, endDate;

        if (crop.targets && crop.targets.length > 0) {
          const validTargets = crop.targets.filter(t => t.min_start && t.max_end);

          if (validTargets.length > 0) {
            const minStarts = validTargets.map(t => new Date(t.min_start));
            startDate = new Date(Math.min(...minStarts));

            const maxEnds = validTargets.map(t => new Date(t.max_end));
            endDate = new Date(Math.max(...maxEnds));
          } else {
            startDate = new Date();
            endDate = new Date();
            endDate.setDate(endDate.getDate() + 30);
          }
        } else {
          startDate = new Date();
          endDate = new Date();
          endDate.setDate(endDate.getDate() + 30);
        }

        const batches = [];
        if (crop.targets && crop.targets.length > 0) {
          crop.targets.forEach(target => {
            if (target.target_subphase_date) {
              batches.push({
                target_subphase_date: target.target_subphase_date,
                target_subphase_name: target.target_subphase_name || 'Unknown Phase',
                batch_id: target.batch_id
              });
            }
          });
        }

        return {
          id: crop.cropplan_id,
          name: crop.crop_name,
          startDate: startDate,
          endDate: endDate,
          color: '#414437',
          plantCount: crop.total_plant_count || 0,
          runStatus: crop.status,
          strains: [{
            strain_name: crop.strain_name,
            batches: batches
          }]
        };
      });
    }
    else if (viewBy === 'strain') {
      const strainEntries = [];
      let entryId = 1;

      data.forEach((strain) => {
        const strainName = strain.strain_name || 'Unknown Strain';
        const crops = strain.cropplans || [];

        if (crops.length === 0) return;

        const processedCrops = crops.map((crop) => {
          let cropStartDate = null;
          let cropEndDate = null;
          const batches = [];

          (crop.targets || []).forEach((target) => {
            const minStart = target.min_start ? new Date(target.min_start) : null;
            const maxEnd = target.max_end ? new Date(target.max_end) : null;

            if (minStart && (!cropStartDate || minStart < cropStartDate)) cropStartDate = minStart;
            if (maxEnd && (!cropEndDate || maxEnd > cropEndDate)) cropEndDate = maxEnd;

            if (target.target_subphase_date) {
              batches.push({
                batch_id: target.batch_id,
                plant_count: target.plant_count || 0,
                target_subphase_name: target.target_subphase_name || 'Unknown Phase',
                target_subphase_date: target.target_subphase_date,
                cropName: crop.crop_name,
              });
            }
          });

          if (!cropStartDate) cropStartDate = new Date();
          if (!cropEndDate) cropEndDate = new Date(cropStartDate.getTime() + 30 * 24 * 60 * 60 * 1000);

          return {
            cropId: crop.cropplan_id,
            cropName: crop.crop_name,
            startDate: cropStartDate,
            endDate: cropEndDate,
            plantCount: crop.total_plant_count || 0,
            batches: batches,
            name: strainName,
            status: crop.status || crop.plan_status || "Unknown"
          };
        });

        if (processedCrops.length === 0) return;

        processedCrops.sort((a, b) => a.startDate - b.startDate);

        // Group crops into lines based on overlap
        const lines = [];

        processedCrops.forEach((crop) => {
          let placed = false;

          // Try to place in an existing line
          for (let line of lines) {
            const lastCropInLine = line[line.length - 1];

            // If this crop doesn't overlap with the last crop in the line
            if (crop.startDate >= lastCropInLine.endDate) {
              line.push(crop);
              placed = true;
              break;
            }
          }

          // If it couldn't be placed, create a new line
          if (!placed) {
            lines.push([crop]);
          }
        });

        // Now create entries for each line
        lines.forEach((line) => {
          if (line.length === 1) {
            // Single crop on this line
            const crop = line[0];
            strainEntries.push({
              id: `${crop.cropId}`,
              name: `${strainName} | ${crop.cropName}`,
              startDate: crop.startDate,
              endDate: crop.endDate,
              color: '#414437',
              plantCount: crop.plantCount,
              runStatus: crop.status,
              strains: [{ strain_name: strainName, batches: crop.batches }],
              crops: [crop.cropName],
              cropIds: [crop.cropId],
              cropCount: 1,
              strainGroup: strainName,
              isMultiCropLine: false
            });
          } else {
            // Multiple crops on this line (non-overlapping)
            const cropIds = line.map(c => c.cropId);
            const cropNames = line.map(c => c.cropName);
            const allBatches = line.flatMap(c => c.batches);
            const totalPlantCount = line.reduce((sum, c) => sum + c.plantCount, 0);

            const overallStartDate = line[0].startDate;
            const overallEndDate = line[line.length - 1].endDate;

            strainEntries.push({
              id: `strain_${entryId++}`,
              name: `${strainName}`,
              startDate: overallStartDate,
              endDate: overallEndDate,
              color: '#414437',
              plantCount: totalPlantCount,
              // FIXED — combine statuses
              runStatus: [...new Set(line.map(c => c.status))].join(" | "),

              strains: [{ strain_name: strainName, batches: allBatches }],
              crops: cropNames,
              cropIds: cropIds,
              cropCount: line.length,
              strainGroup: strainName,
              isMultiCropLine: true,
              individualCrops: line
            });
          }
        });
      });
      return strainEntries;
    }

    return [];
  }, [data, viewBy]);

  const tooltip = d3.select("body")
                    .append("div")
                    .attr("class", "crop-tooltip")
                    .style("position", "absolute")
                    .style("background", "#1e1e1e")
                    .style("color", "#fff")
                    .style("padding", "8px 12px")
                    .style("border-radius", "6px")
                    .style("font-size", "12px")
                    .style("pointer-events", "none")
                    .style("opacity", 0)
                    .style("z-index", 1000);

  const timelineWidthData = React.useMemo(() => {
    if (processedCropData.length === 0) {
      return { timelineWidth: containerWidth, totalMonths: 11, displayMonths: 12 };

    }

    const overallMinDate = processedCropData.reduce((min, crop) =>
      crop.startDate < min ? crop.startDate : min, processedCropData[0].startDate
    );
    const overallMaxDate = processedCropData.reduce((max, crop) =>
      crop.endDate > max ? crop.endDate : max, processedCropData[0].endDate
    );

    const minDateWithPadding = new Date(overallMinDate);
    minDateWithPadding.setMonth(minDateWithPadding.getMonth() - 3);
    const maxDateWithPadding = new Date(overallMaxDate);
    maxDateWithPadding.setMonth(maxDateWithPadding.getMonth() + 3);

    const monthStartRange = d3.timeMonth.floor(minDateWithPadding);
    const monthEndRange = d3.timeMonth.ceil(maxDateWithPadding);
    const totalMonths = d3.timeMonth.count(monthStartRange, monthEndRange);

    // const minTimelineWidth = containerWidth;
    // const calculatedWidth = Math.max(minTimelineWidth, totalMonths * 120);
    let displayMonths = 12;
    if (timeRange === '3M') {
      displayMonths = 3;
    }
    else if (timeRange === '6M') {
      displayMonths = 6;
    }
    else if (timeRange === '2Y'){
      displayMonths= 24;
    }
    else if (timeRange === '3Y'){
      displayMonths= 36;
    }
    else if (timeRange === '1Y') {
      displayMonths = 12;
    }
    const monthWidth = containerWidth / displayMonths;
    const calculatedWidth = totalMonths * monthWidth

    return { timelineWidth: calculatedWidth, totalMonths, displayMonths, monthWidth };
  }, [processedCropData, timeRange, containerWidth]);

  const barSpacing = 80;
  const baseHeight = 200;
  const phaseHeight = 40;
  const subphaseHeight = 24;
  const gap = 5;
  


  const expandedPhase = phaseData?.find(p => p.id === expandedPhaseId);
  let routineExtraHeight = 0;

  if (expandedPhase && expandedSubphaseId) {
    const selectedSub = expandedPhase.subphases.find(s => s.id === expandedSubphaseId);

    if (selectedSub?.routines?.length) {
      routineExtraHeight = selectedSub.routines.length * (subphaseHeight + gap);
    }
  }

  const dynamicHeight =
  baseHeight +
  (processedCropData.length * barSpacing) +
  (phaseData ? phaseData.length * (phaseHeight + gap) : 0) +
  (expandedPhase ? expandedPhase.subphases.length * (subphaseHeight + gap) : 0) +
  routineExtraHeight;
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const { width, height } = entry.contentRect;

        if (width > 0) setContainerWidth(width);
      }
    });

    observer.observe(container);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const headerSvg = d3.select(headerSvgRef.current);
    headerSvg.selectAll("*").remove();

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const containerWidth = containerRef.current?.clientWidth || containerWidth;

    const margin = { top: 20, right: 40, bottom: 40, left: 40 };
    const headerHeight = 100;
    const width = containerWidth - margin.left - margin.right;
    const height = dynamicHeight - headerHeight - margin.bottom;

    const calculatedTimelineWidth = timelineWidthData.timelineWidth;

    let overallMinDate, overallMaxDate;

    if (processedCropData.length > 0) {
      overallMinDate = processedCropData.reduce((min, crop) =>
        crop.startDate < min ? crop.startDate : min, processedCropData[0].startDate
      );

      overallMaxDate = processedCropData.reduce((max, crop) =>
        crop.endDate > max ? crop.endDate : max, processedCropData[0].endDate
      );

      overallMinDate = new Date(overallMinDate);
      overallMinDate.setMonth(overallMinDate.getMonth() - 2);

      overallMaxDate = new Date(overallMaxDate);
      overallMaxDate.setMonth(overallMaxDate.getMonth() + 2);
    } else {
      overallMinDate = new Date('2024-01-01');
      overallMaxDate = new Date('2024-12-31');
    }

    const minDate = overallMinDate;
    const maxDate = overallMaxDate;

    const xScale = d3.scaleTime()
      .domain([minDate, maxDate])
      .range([0, calculatedTimelineWidth]);

    const headerG = headerSvg.append("g")
      .attr("transform", `translate(${margin.left},10)`);

    const monthStartRange = d3.timeMonth.floor(minDate);
    const monthEndRange = d3.timeMonth.ceil(maxDate);
    const monthTicks = d3.timeMonth.range(monthStartRange, monthEndRange);

    const totalMonths = monthTicks.length;
    const timelineWidth = width * 3;
    const monthWidth = totalMonths > 0 ? timelineWidth / totalMonths : width;
    let monthSkip = 1;

    if (timeRange === '2Y') {
      monthSkip = 2;
    }
    else if (timeRange === '3Y') {
      monthSkip = 4;
    }

    monthTicks.forEach((monthStart, i) => {
      if (i % monthSkip !== 0) return;
      const monthName = d3.timeFormat("%b %Y")(monthStart);
      const xPosition = xScale(monthStart);

      headerG.append("text")
        .attr("class", "month-label")
        .attr("x", xPosition + monthWidth / 4)
        .attr("y", 20)
        .attr("text-anchor", "middle")
        .attr("font-size", "14px")
        .attr("font-weight", "600")
        .attr("fill", theme.svgText)
        .text(monthName);
    });

    const dayNumbers = [5, 10, 15, 20, 25];

    monthTicks.forEach((monthStart) => {
      const monthEnd = d3.timeMonth.offset(monthStart, 1);
      const daysInMonth = d3.timeDay.count(monthStart, monthEnd);
      if (timeRange === '3M' || timeRange === '6M' || timeRange === '1Y') {
        dayNumbers.forEach((dayNum) => {
          if (dayNum <= daysInMonth && dayNum > 0) {
            const dayDate = new Date(monthStart);
            dayDate.setDate(dayNum);

            if (dayDate >= minDate && dayDate <= maxDate) {
              const xPosition = xScale(dayDate);

              headerG.append("text")
                .attr("class", "day-tick")
                .attr("x", xPosition)
                .attr("y", 65)
                .attr("text-anchor", "middle")
                .attr("font-size", "11px")
                .attr("fill", theme.textMuted)
                .text(dayNum);
            }
          }
        });
      }
    });

    const g = svg.append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    g.append("rect")
      .attr("x", 0)
      .attr("y", 0)
      .attr("width", calculatedTimelineWidth)
      .attr("fill", theme.svgBg)
      .style("opacity", 1);

    const barHeight = 30;
    let currentY = 30;
    let selectedCropY = null;

    processedCropData.forEach((crop, index) => {
      const yPosition = currentY;
      if (crop.id === expandedCropId) {
        selectedCropY = yPosition;
      }

      const taskCounts = viewBy === 'crop'
        ? getTaskCountsForCrop(crop.id)
        : getTaskCountsForStrain(crop.cropIds || []);

      // Check if this is a multi-crop line (multiple bars on same line)
      if (crop.isMultiCropLine && crop.individualCrops) {
        // Render multiple bars on the same line
        crop.individualCrops.forEach((individualCrop, cropIdx) => {
          const barWidth = xScale(individualCrop.endDate) - xScale(individualCrop.startDate);
          const individualTaskCounts = getTaskCountsForCrop(individualCrop.cropId);
          const cropId = individualCrop.cropId ? individualCrop.cropId.toString().replace('CropRun_', '') : '';
          const statusColor = STATUS_COLORS[individualCrop.status] || "#ccc";

          // Render individual bar or diamond based on view
          if (chartView === 'chart') {
            g.append("rect")
              .attr("x", xScale(individualCrop.startDate))
              .attr("y", yPosition - barHeight / 2)
              .attr("width", barWidth)
              .attr("height", barHeight)
              .attr("fill", crop.color)
              .attr("rx", 6)
              .attr("ry", 6)
              .style("opacity", 0.9)
              .style("stroke", "#ffffff")
              .style("stroke-width", 1)
              .style("cursor", "pointer")
              .append("title")
              .text(`C${cropId}`);
          } else if (chartView === 'diamond') {
            // Label logic moved inside the diamond loop below
          }


          // Add target dates for this individual crop
          const processedDates = new Set();
          individualCrop.batches.forEach(batch => {
            if (batch.target_subphase_date) {
              const targetDate = new Date(batch.target_subphase_date);
              const dateString = targetDate.toDateString();

              // Deduplicate: If we already processed this date, skip (unless chartView is chart, where we might want all? user said unique date -> unique diamond)
              // User requirement: "if any crop having 2 diffrent plan and diffrent date so it will come 2 time; if 2 phaseplan but same date so it will show 1 time"
              // The logic implies we group by Date.

              if (targetDate >= minDate && targetDate <= maxDate) {
                if (chartView === 'diamond') {
                  // Diamond View Logic
                  if (!processedDates.has(dateString)) {
                    processedDates.add(dateString);
                    const dotX = xScale(targetDate);
                    // Align diamond on the row center
                    const dotY = yPosition;

                    // const diamondSymbol = d3.symbol().type(d3.symbolDiamond).size(150);

                    // Replaced with Rounded Rect Diamond
                    g.append("rect")
                      .attr("x", -5)
                      .attr("y", -5)
                      .attr("width", 10)
                      .attr("height", 10)
                      .attr("rx", 1)
                      .attr("transform", `translate(${dotX}, ${dotY}) rotate(45)`)
                      .attr("fill", statusColor)
                      // .attr("stroke", "white") // User's example didn't have stroke, but previous D3 did. Removing to match 'clean' look or keep? User said "use same dimond". The user example in SVG has no stroke. I will remove stroke to match.
                      // .attr("stroke-width", 1)
                      .style("cursor", "pointer")
                      .append("title")
                      .text(`${individualCrop.cropName}\nTarget: ${batch.target_subphase_name}\nDate: ${targetDate.toLocaleDateString()}`);

                    // Label above EACH diamond
                    const labelText = (viewBy === 'strain' && individualCrop.strainGroup) ? individualCrop.strainGroup : individualCrop.name;
                    g.append("text")
                      .attr("x", dotX)
                      .attr("y", dotY - 20)
                      .attr("text-anchor", "middle")
                      .attr("fill", theme.svgText)
                      .style("font-family", "Space Grotesk")
                      .style("font-size", "12px")
                      .style("font-weight", 400)
                      .text(labelText);
                  }
                } else {
                  // Start/End markers (only in Chart View)
                  g.append("circle")
                    .attr("cx", xScale(individualCrop.startDate))
                    .attr("cy", yPosition)
                    .attr("r", 8)
                    .attr("fill", "transparent")
                    .attr("stroke", "transparent")
                    .style("cursor", "pointer")
                    .on("mouseover", function () {
                      d3.select(this)
                        .attr("fill", "white")
                        .attr("stroke", crop.color)
                        .attr("stroke-width", 2)
                        .attr("r", 4);
                    })
                    .on("mouseout", function () {
                      d3.select(this)
                        .attr("fill", "transparent")
                        .attr("stroke", "transparent")
                        .attr("r", 8);
                    })
                    .append("title")
                    .text(`${individualCrop.cropName}\nStart: ${individualCrop.startDate.toLocaleDateString()}`);

                  g.append("circle")
                    .attr("cx", xScale(individualCrop.endDate))
                    .attr("cy", yPosition)
                    .attr("r", 8)
                    .attr("fill", "transparent")
                    .attr("stroke", "transparent")
                    .style("cursor", "pointer")
                    .on("mouseover", function () {
                      d3.select(this)
                        .attr("fill", "white")
                        .attr("stroke", crop.color)
                        .attr("stroke-width", 2)
                        .attr("r", 4);
                    })
                    .on("mouseout", function () {
                      d3.select(this)
                        .attr("fill", "transparent")
                        .attr("stroke", "transparent")
                        .attr("r", 8);
                    })
                    .append("title")
                    .text(`${individualCrop.cropName}\nEnd: ${individualCrop.endDate.toLocaleDateString()}`);

                  // Standard Dot Logic for Chart View
                  const dotX = xScale(targetDate);
                  const dotY = yPosition - barHeight / 2 - 8;

                  g.append("circle")
                    .attr("cx", dotX)
                    .attr("cy", dotY)
                    .attr("r", 5)
                    .attr("fill", "#B1D23A")
                    .attr("stroke", "white")
                    .attr("stroke-width", 2)
                    .style("cursor", "pointer")
                    .append("title")
                    .text(`${individualCrop.cropName}\nTarget: ${batch.target_subphase_name}\nDate: ${targetDate.toLocaleDateString()}`);
                }
              }
            }
          });

          if (chartView === 'chart') {
            // Icons for EACH bar
            // Get task counts for this specific crop
            g.append("image")
              .attr("href", "/Metrc.png")
              .attr("x", xScale(individualCrop.startDate) + 20)
              .attr("y", yPosition - barHeight / 2 - 20)
              .attr("width", 16)
              .attr("height", 16)
              .style("cursor", "pointer")
              .on("click", () => {
                handleIconClick(cropId, 'validate');
              })
              .append("title")
              .text("State of Validate");

            g.append("text")
              .attr("x", xScale(individualCrop.startDate) + 40)
              .attr("y", yPosition - barHeight / 2 - 8)
              .attr("fill", theme.svgText)
              .attr("font-size", "12px")
              .text(individualTaskCounts.validateCount);

            // Then in the icon:
            g.append("image")
              .attr("href", "/Bell.png")
              .attr("x", xScale(individualCrop.startDate) + 70)
              .attr("y", yPosition - barHeight / 2 - 20)
              .attr("width", 16)
              .attr("height", 16)
              .style("cursor", "pointer")
              .on("click", () => {
                handleIconClick(cropId, 'alert');
              })
              .append("title")
              .text("State of Alert");

            g.append("text")
              .attr("x", xScale(individualCrop.startDate) + 90)
              .attr("y", yPosition - barHeight / 2 - 8)
              .attr("fill", "white")
              .attr("font-size", "12px")
              .text(individualTaskCounts.alertCount);

            // OOC icon
            g.append("svg")
              .attr("xlink:href", "/OOC.png")
              .attr("x", xScale(individualCrop.startDate) + 120)
              .attr("y", yPosition - barHeight / 2 - 20)
              .attr("width", 16)
              .attr("height", 16)
              .style("cursor", "pointer")
              .on("click", () => {
                handleIconClick(cropId, 'event');
              })
              .append("title")
              .text("State of Event");

            g.append("text")
              .attr("x", xScale(individualCrop.startDate) + 140)
              .attr("y", yPosition - barHeight / 2 - 8)
              .attr("fill", "white")
              .attr("font-size", "12px")
              .text(individualTaskCounts.routineCount);

            // External link icon for each crop

            const cropURL = `${window.location.origin}/crops/runs/${cropId}/?tab=overview`;

            g.append("image")
              .attr("xlink:href", "/external.png")
              .attr("x", xScale(individualCrop.startDate) + 170)
              .attr("y", yPosition - barHeight / 2 - 23)
              .attr("width", 20)
              .attr("height", 20)
              .style("cursor", "pointer")
              .on("click", () => {
                if (cropId) {
                  window.open(cropURL);
                }
              })
              .append("title")
              .text("Crop Run");

            // Crop name for each bar
            g.append("text")
              .attr("x", xScale(individualCrop.startDate) + 20)
              .attr("y", yPosition + 5)
              .attr("fill", "white")
              .style("font-family", "Space Grotesk")
              .style("font-size", "14px")
              .style("font-weight", 400)
              .text(`${individualCrop.name} | ${individualCrop.cropName} | ${individualCrop.plantCount} plants`);

            // Status indicator for each bar
            g.append("text")
              .attr("x", xScale(individualCrop.endDate) - 10)
              .attr("y", yPosition + 5)
              // .attr("fill", '#4CAF50')
              .attr("fill", statusColor)
              .attr("font-size", "14px")
              .attr("text-anchor", "end")
              .attr("font-weight", "bold")
              .text(individualCrop.status || "Unknown");
          }
        });
      }
      else {
        // Single bar
        const barWidth = xScale(crop.endDate) - xScale(crop.startDate);
        // Around line 445 in the single bar section
        const cropId = crop.id ? crop.id.toString().replace('CropRun_', '') : '';

        // Modify the single bar rect:
        if (chartView === 'chart') {

          const fullText = `${crop.name} | ${crop.plantCount || 0} plants`;
          g.append("rect")
            .attr("x", xScale(crop.startDate))
            .attr("y", yPosition - barHeight / 2)
            .attr("width", barWidth)
            .attr("height", barHeight)
            .attr("fill", theme.rect)
            .on("click", (event) => {
              event.stopPropagation()
              handleRectClick(crop.id);
            })
            g.append("rect")
            .attr("x", xScale(crop.startDate))
            .attr("y", yPosition - barHeight / 2)
            .attr("width", barWidth)
            .attr("height", barHeight)
            .attr("fill", theme.rect)
            .on("click", (event) => {
              event.stopPropagation()
              d3.selectAll(".crop-tooltip").style("opacity", 0);
              handleRectClick(crop.id);
            })
            .on("mouseover", (event) => {
              tooltip
                .style("opacity", 1)
                .html(`
                  <div><strong>${crop.name}</strong></div>
                  <div>${crop.plantCount || 0} plants</div>
                `);
            })
            .on("mousemove", (event) => {
              tooltip
                .style("left", event.pageX + 12 + "px")
                .style("top", event.pageY + 12 + "px");
            })
            .on("mouseout", () => {
              tooltip.style("opacity", 0);
            })
            .attr("rx", 6)
            .attr("ry", 6)
            .style("opacity", 0.9)
            .style("stroke", theme.text)
            .style("stroke-width", 1)
            .style("cursor", "pointer") // Add cursor pointer
            .append("title")
            .text(`C${cropId}`);
            if (expandedCropId === crop.id && phaseData) {
              const phaseHeight = 30;
              let phaseCurrentY = yPosition + barHeight / 2 + gap;
              phaseData.forEach((phase,i) => {
                const phaseBlockY = phaseCurrentY

                let extraHeight = phaseHeight + gap;

                g.append("rect")
                  .attr("x", xScale(crop.startDate))
                  .attr("y", phaseBlockY)
                  .attr("width", barWidth)
                  .attr("height", phaseHeight)
                  .attr("fill", theme.cardBg)
                  .style("stroke", theme.accent)
                  .attr("rx", 4)
                  .style("stroke-width", 1)
                  .style("cursor", "pointer")
                  .on("click", () => handlePhaseClick(phase.id));

                g.append("text")
                  .attr("x", xScale(crop.startDate) + 10)
                  .attr("y", phaseBlockY + phaseHeight / 2 + 4)
                  .attr("fill", theme.svgText)
                  .style("font-size", "13px")
                  .style("font-weight", "500")
                  .style("font-family", "Space Grotesk")
                  .text(phase.name); 

                if (expandedPhaseId === phase.id) {
                  const subphaseY = phaseBlockY + phaseHeight + gap;
                  const subphaseHeight = 24;
                  const totalDays = Math.max(...phase.subphases.map(s => s.end_day));
                  let subphaseCurrentY = subphaseY;
                  
                  phase.subphases.forEach((subphase, si) => {
                    const subX = xScale(crop.startDate) + (subphase.start_day / totalDays) * barWidth;
                    const subW = ((subphase.end_day - subphase.start_day) / totalDays) * barWidth;
                    const rowY = subphaseCurrentY;
                    const isComplete = isSubphaseComplete(subphase);

                    g.append("rect")
                      .attr("x", subX)
                      .attr("y", rowY)
                      .attr("width", Math.max(subW, 4))
                      .attr("height", subphaseHeight)
                      .attr("fill", theme.innerCardBg)
                      .attr("rx", 3)
                      .style("stroke", theme.textFaint)
                      .style("stroke-width", 1)

                    g.append("text")
                      .attr("x", subX + 3)
                      .attr("y", rowY + subphaseHeight / 2 + 4)
                      .attr("fill", isComplete ? theme.accent : theme.textMuted)
                      .style("font-size", "12px")
                      .style("font-weight", "400")
                      .style("font-family", "Space Grotesk")
                      .text(`${isComplete ? "✓ " : ""}P${si + 1}: ${subphase.start_day}-${subphase.end_day} ${subphase.name}`)
                      .style("cursor", "pointer")
                      .on("click", () => {
                        const isClosing = expandedSubphaseId === subphase.id;
                        setExpandedSubphaseId(prev => prev === subphase.id ? null : subphase.id);

                        setActiveSubphase(isClosing ? null : {
                          phase: phase,
                          subphase: subphase
                        });
                      });

                      subphaseCurrentY += subphaseHeight + gap;

                      if (expandedSubphaseId === subphase.id && subphase.routines?.length) {
                        const routineHeight = subphaseHeight;
                        const routineGap = 4;

                        subphase.routines.forEach((routine, ri) => {
                          const routineY = subphaseCurrentY;

                          g.append("rect")
                            .attr("x", subX)
                            .attr("y", routineY)
                            .attr("width", Math.max(subW, 4))
                            .attr("height", routineHeight)
                            .attr("fill", "#1976D2")
                            .attr("rx", 3);

                          g.append("text")
                            .attr("x", subX + 5)
                            .attr("y", routineY + routineHeight / 2 + 4)
                            .attr("fill", theme.svgText)
                            .style("font-size", "12px")
                            .style("font-family", "Space Grotesk")

                            .text(routine.name)
                            .on("click", (event) => {
                              event.stopPropagation();

                              console.log("ROUTINE CLICKED:", routine);
                              APIService.fetchInstance("new/cropplan/")
                                .then(res => {
                                  setAssignees(res.data.assignees);
                                  setUsers(res.data.users);
                                });
                              setSelectedRoutine({
                                ...routine,
                                subphase,
                                cropplan_id: cropId
                              });

                            })
                            .style("cursor", "pointer");
                            
                          subphaseCurrentY += routineHeight + gap;
                        });

                        

                        
                      }
                    
                  });
                  extraHeight += phase.subphases.length * (subphaseHeight + gap);

                }
                phaseCurrentY += extraHeight;
              })
                
            }
        } else if (chartView === 'diamond') {
          // Label logic moved inside the diamond loop below
        }

        // Target dates
        const targetDates = new Set();
        const processedDateStrings = new Set();

        if (crop.strains && crop.strains.length > 0) {
          crop.strains.forEach(strain => {
            if (strain.batches && strain.batches.length > 0) {
              strain.batches.forEach(batch => {
                if (batch.target_subphase_date) {
                  targetDates.add(JSON.stringify({
                    date: batch.target_subphase_date,
                    name: batch.target_subphase_name || 'Unknown Phase'
                  }));
                }
              });
            }
          });
        }

        targetDates.forEach(dateStr => {
          const targetDateObj = JSON.parse(dateStr);
          const targetDate = new Date(targetDateObj.date);
          const dateString = targetDate.toDateString();

          if (targetDate >= minDate && targetDate <= maxDate) {
            if (chartView === 'diamond') {
              if (!processedDateStrings.has(dateString)) {
                processedDateStrings.add(dateString);
                const dotX = xScale(targetDate);
                const dotY = yPosition;

                // const diamondSymbol = d3.symbol().type(d3.symbolDiamond).size(150);

                // Replaced with Rounded Rect Diamond
                g.append("rect")
                  .attr("x", -5)
                  .attr("y", -5)
                  .attr("width", 10)
                  .attr("height", 10)
                  .attr("rx", 1)
                  .attr("transform", `translate(${dotX}, ${dotY}) rotate(45)`)
                  .attr("fill", STATUS_COLORS[crop.runStatus] || "#FFD700")
                  // .attr("stroke", "white")
                  // .attr("stroke-width", 1)
                  .style("cursor", "pointer")
                  .append("title")
                  .text(`Target subphase: ${targetDateObj.name}\nTarget Date: ${targetDate.toLocaleDateString()}`);

                // Label above EACH diamond
                const labelText = (viewBy === 'strain' && crop.strainGroup) ? crop.strainGroup : crop.name;
                g.append("text")
                  .attr("x", dotX)
                  .attr("y", dotY - 20)
                  .attr("text-anchor", "middle")
                  .attr("fill", theme.svgText)
                  .style("font-family", "Space Grotesk")
                  .style("font-size", "12px")
                  .style("font-weight", 400)
                  .text(labelText);
              }
            } else {
              // Chart view dots
              const dotX = xScale(targetDate);
              const dotY = yPosition - barHeight / 2 - 8;
              g.append("circle")
                .attr("cx", dotX)
                .attr("cy", dotY)
                .attr("r", 5)
                .attr("fill", "#B1D23A")
                .attr("stroke", "white")
                .attr("stroke-width", 2)
                .style("cursor", "pointer")
                .append("title")
                .text(`Target subphase: ${targetDateObj.name}\nTarget Date: ${targetDate.toLocaleDateString()}`);
            }
          }
        });

        if (chartView === 'chart') {
          // Start/End markers
          g.append("circle")
            .attr("cx", xScale(crop.startDate))
            .attr("cy", yPosition)
            .attr("r", 8)
            .attr("fill", "transparent")
            .attr("stroke", "transparent")
            .style("cursor", "pointer")
            .on("mouseover", function () {
              d3.select(this).attr("fill", theme.svgText).attr("stroke", crop.color).attr("stroke-width", 2).attr("r", 4);
            })
            .on("mouseout", function () {
              d3.select(this).attr("fill", "transparent").attr("stroke", "transparent").attr("r", 8);
            })
            .append("title")
            .text(`Start Date: ${crop.startDate.toLocaleDateString()}`);

          g.append("circle")
            .attr("cx", xScale(crop.endDate))
            .attr("cy", yPosition)
            .attr("r", 8)
            .attr("fill", "transparent")
            .attr("stroke", "transparent")
            .style("cursor", "pointer")
            .on("mouseover", function () {
              d3.select(this).attr("fill", "white").attr("stroke", crop.color).attr("stroke-width", 2).attr("r", 4);
            })
            .on("mouseout", function () {
              d3.select(this).attr("fill", "transparent").attr("stroke", "transparent").attr("r", 8);
            })
            .append("title")
            .text(`End Date: ${crop.endDate.toLocaleDateString()}`);
        }
        if (chartView === 'chart') {
          g.append("image")
            .attr("href", "/Metrc.png")
            .attr("x", xScale(crop.startDate) + 20)
            .attr("y", yPosition - barHeight / 2 - 20)
            .attr("width", 16)
            .attr("height", 16)
            .style("cursor", "pointer")
            .on("click", () => {
              handleIconClick(cropId, 'validate');
            })
            .append("title")
            .text("State of validate");

          g.append("text")
            .attr("x", xScale(crop.startDate) + 40)
            .attr("y", yPosition - barHeight / 2 - 8)
            .attr("fill", theme.text)
            .attr("font-size", "12px")
            .text(taskCounts.validateCount);

          // Then in the icon click handlers:
          g.append("svg")
            .attr("x", xScale(crop.startDate) + 70)
            .attr("y", yPosition - barHeight / 2 - 20)
            .attr("width", 16)
            .attr("height", 16)
            .attr("viewBox", "0 0 24 24") 
            .attr("fill", theme.icon)
            .style("cursor", "pointer")
            .on("click", () => {
              handleIconClick(cropId, 'alert');
            })
            .append("path")
            .attr("d", "M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2m6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1zm-2 1H8v-6c0-2.48 1.51-4.5 4-4.5s4 2.02 4 4.5z")
            .attr("fill", theme.icon)
            .append("title")
            .text("State of Alert");            

          g.append("text")
            .attr("x", xScale(crop.startDate) + 90)
            .attr("y", yPosition - barHeight / 2 - 8)
            .attr("fill", theme.text)
            .attr("font-size", "12px")
            .text(taskCounts.alertCount);

          g.append("svg")
            .attr("x", xScale(crop.startDate) + 120)
            .attr("y", yPosition - barHeight / 2 - 20)
            .attr("width", 16)
            .attr("height", 16)
            .attr("viewBox", "0 0 24 24") 
            .style("cursor", "pointer")
            .on("click", () => {
              handleIconClick(cropId, 'event');
            })
            .append("path")
            .attr("d","M12 2C6.49 2 2 6.49 2 12s4.49 10 10 10 10-4.49 10-10S17.51 2 12 2m0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8m3-8c0 1.66-1.34 3-3 3s-3-1.34-3-3 1.34-3 3-3 3 1.34 3 3")
            .attr("fill",theme.icon)
            .append("title")
            .text("State of Event");

          g.append("text")
            .attr("x", xScale(crop.startDate) + 140)
            .attr("y", yPosition - barHeight / 2 - 8)
            .attr("fill", theme.text)
            .attr("font-size", "12px")
            .text(taskCounts.routineCount);

          // const cropId = crop.id ? crop.id.toString().replace('CropRun_', '') : '';

          const cropURL = `${window.location.origin}/crops/runs/${cropId}/?tab=overview`;

          g.append("svg")
            .attr("x", xScale(crop.startDate) + 170)
            .attr("y", yPosition - barHeight / 2 - 23)
            .attr("width", 20)
            .attr("height", 20)
            .attr("viewBox", "0 0 24 24") 
            .style("cursor", "pointer")
            .on("click", () => {
              if (cropId) window.open(cropURL);
            })
            .append("path")
            .attr("d","M19 19H5V5h7V3H3v18h18v-9h-2zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3z")
            .attr("fill", theme.icon)
            .append("title")
            .text("Crop Run");
          const barStart = xScale(crop.startDate);
          const barEnd = xScale(crop.endDate);
          const barWidth = barEnd - barStart;

          const statusText = crop.runStatus || 'Unknown';
          const fullText = `${crop.name} | ${crop.plantCount || 0} plants`;

          // helper
          const getTextWidth = (text, font = "14px Space Grotesk") => {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            ctx.font = font;
            return ctx.measureText(text).width;
          };

          const statusWidth = getTextWidth(statusText, "bold 14px Space Grotesk");

          let displayText = fullText;

          // space left after reserving status
          const availableWidth = barWidth - statusWidth - 50;

          if (availableWidth < 50) {
            // very small → just ID
            displayText = `C${crop.id}`;
          }
          else if (availableWidth < 120) {
            // medium → short name
            displayText = crop.name.slice(0, 5) + "...";
          }
          else if (availableWidth < getTextWidth(fullText)) {
            // large but not enough → truncate
            displayText = fullText.slice(0, Math.floor(availableWidth / 7)) + "...";
          }
          //labels
          g.append("text")
            .attr("x", xScale(crop.startDate) + 20)
            .attr("y", yPosition + 5)
            .attr("fill", theme.text)
            .style("font-family", "Space Grotesk")
            .style("font-size", "14px")
            .style("font-weight", 400)
            .text(displayText);

          if (viewBy === 'strain' && crop.cropCount > 1) {
            g.append("text")
              .attr("x", xScale(crop.startDate) + 20)
              .attr("y", yPosition + 20)
              .attr("fill", "#ddd")
              .attr("font-size", "11px")
              .style("font-family", "Space Grotesk")
              .style("font-size", "18px")
              .style("font-weight", 400)
            // .text(`${crop.cropCount} crops: ${crop.crops.join(', ')}`);
          }
          g.append("text")
            .attr("x", xScale(crop.endDate) - 20)
            .attr("y", yPosition + 5)
            // .attr("fill", crop.runStatus === 'Ready For Grow' ? '#4CAF50' : crop.runStatus === 'Planning' ? '#2196F3' : '#FF9800')
            .attr("fill", STATUS_COLORS[crop.runStatus] || "#ccc")
            .attr("font-size", "14px")
            .attr("text-anchor", "end")
            .attr("font-weight", "bold")
            .text(statusText);
        }
      }
      currentY += barSpacing;
      if (expandedCropId === crop.id && phaseData) {
        let totalHeight = 0;

        phaseData.forEach((phase) => {
          totalHeight += phaseHeight + gap;

          if (expandedPhaseId === phase.id) {
            phase.subphases.forEach((subphase) => {
              totalHeight += subphaseHeight + gap;

              if (expandedSubphaseId === subphase.id && subphase.routines?.length) {
                totalHeight += subphase.routines.length * (subphaseHeight + gap);
              }
            });
          }
        });

        currentY += totalHeight;
      }
    });
    selectedCropYRef.current = selectedCropY;

    // Month separator lines
    monthTicks.forEach((monthStart, index) => {
      if (index % monthSkip !== 0) return;
      
      if (index > 0) {
        const xPosition = xScale(monthStart);

        g.append("line")
          .attr("x1", xPosition)
          .attr("x2", xPosition)
          .attr("y1", 0)
          .attr("y2", height - 20)
          .attr("stroke", theme.border)
          .attr("stroke-width", 1)
          .attr("stroke-dasharray", "2,2")
          .style("opacity", 0.5);
      }
    });
    // Today line
    const today = new Date();
    if (today >= minDate && today <= maxDate) {
      const todayX = xScale(today);

      g.append("line")
        .attr("x1", todayX)
        .attr("x2", todayX)
        .attr("y1", 0)
        .attr("y2", height - 20)
        .attr("stroke", "#f10a0aea")
        .attr("stroke-width", 2)
        .style("opacity", 0.8);

      g.append("text")
        .attr("x", todayX - 28)
        .attr("y", -8)
        .attr("fill", theme.textMuted)
        .attr("font-size", "13px")
        .attr("font-weight", "bold")
        .text("Today")
        .text(d3.timeFormat("%d %b %Y")(today));
    }

    d3.select(headerSvgRef.current).attr("height", headerHeight);
    d3.select(svgRef.current).attr("height", dynamicHeight - headerHeight);

  }, [processedCropData, selectedYear, dynamicHeight, viewBy, timelineWidthData, barSpacing, routine, validate, alert, onSwitchToCommandView, timeRange, chartView, containerWidth, expandedCropId,expandedPhaseId,phaseData]);


  // Synchronize horizontal scrolling between header and content
  useEffect(() => {
    const headerContainer = headerContainerRef.current;
    const contentContainer = containerRef.current;

    if (!headerContainer || !contentContainer) return;

    const syncScroll = (source, target) => {
      return () => {
        target.scrollLeft = source.scrollLeft;
      };
    };

    const contentScrollHandler = syncScroll(contentContainer, headerContainer);
    const headerScrollHandler = syncScroll(headerContainer, contentContainer);

    contentContainer.addEventListener('scroll', contentScrollHandler);
    headerContainer.addEventListener('scroll', headerScrollHandler);

    return () => {
      contentContainer.removeEventListener('scroll', contentScrollHandler);
      headerContainer.removeEventListener('scroll', headerScrollHandler);
    };
  }, []);

  useEffect(() => {
    if (!containerRef.current || selectedCropYRef.current == null) return;

    const container = containerRef.current;

    const containerTop = container.scrollTop;
    const containerBottom = containerTop + container.clientHeight;

    const targetY = selectedCropYRef.current;

    // Only scroll if out of view
    if (targetY < containerTop || targetY > containerBottom) {
      container.scrollTo({
        top: targetY - 100,
        behavior: "smooth"
      });
    }

  }, [expandedCropId]);

  useEffect(() => {
    if (processedCropData.length === 0) return;

    const overallMinDate = processedCropData.reduce(
      (min, crop) => (crop.startDate < min ? crop.startDate : min),
      processedCropData[0].startDate
    );

    const overallMaxDate = processedCropData.reduce(
      (max, crop) => (crop.endDate > max ? crop.endDate : max),
      processedCropData[0].endDate
    );

    const minDate = new Date(overallMinDate);
    minDate.setMonth(minDate.getMonth() - 2);

    const maxDate = new Date(overallMaxDate);
    maxDate.setMonth(maxDate.getMonth() + 2);

    setDateDomain([minDate, maxDate]);

  }, [processedCropData]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || !dateDomain) return;

    if (container.scrollWidth <= container.clientWidth) return;

    const today = new Date();

    const xScale = d3.scaleTime()
      .domain(dateDomain)
      .range([0, container.scrollWidth]);

    const todayX = xScale(today);

    const scrollLeft = Math.max(
      0,
      Math.min(
        todayX - container.clientWidth / 2,
        container.scrollWidth - container.clientWidth
      )
    );

    container.scrollLeft = scrollLeft;

  }, [dateDomain, timeRange]);

  const divider = { borderBottom: `1px solid ${theme.divider}`, mt: 2, mb: 0 };

  return (
    <>
    <div
      style={{
        background: theme.headerBg,
        width: '100%',
        height: '100%',
        flex: 1,
        border: '1px solid #444',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Dropdown Header */}
      <div style={{
        padding: '8px 10px',
        borderBottom: '1px solid #444',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '14px',
        color: theme.text,
        whiteSpace: 'nowrap',
        backgroundColor: theme.headerBg,
      }}>
        <span>View by:</span>
        <select
          value={viewBy || 'crop'}
          onChange={(e) => {
            const newValue = e.target.value;
            if (setViewBy && typeof setViewBy === 'function') {
              setViewBy(newValue);
            } else {
              console.error('❌ setViewBy is not a function!', setViewBy);
            }
          }}
          style={{
            backgroundColor: theme.cardBg,
            color: theme.text,
            border: '1px solid #666',
            borderRadius: '4px',
            padding: '8px 8px',
            fontSize: '12px',
            cursor: 'pointer'
          }}
        >
          <option value="crop">Crop</option>
          <option value="strain">Strain</option>
        </select>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '12px' }}>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span
              onClick={() => setChartView('chart')}
              style={{
                color: chartView === 'chart' ? '#B1D23A' : '#ccc',
                fontSize: '13px',
                cursor: 'pointer',
                fontWeight: chartView === 'chart' ? 'bold' : 'normal'
              }}>
              Timeline
            </span>

            <Switch
              checked={chartView === 'diamond'}
              onChange={(e) => setChartView(e.target.checked ? 'diamond' : 'chart')}
            // sx={{
            //   '& .MuiSwitch-switchBase.Mui-checked': {
            //     color: '#B1D23A',
            //   },
            //   '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
            //     backgroundColor: '#B1D23A',
            //     opacity: 0.5
            //   },
            //   '& .MuiSwitch-track': {
            //     backgroundColor: '#666',
            //   }
            // }}
            // size="small"
            />

            <span
              onClick={() => setChartView('diamond')}
              style={{
                color: chartView === 'diamond' ? '#B1D23A' : '#ccc',
                fontSize: '13px',
                cursor: 'pointer',
                fontWeight: chartView === 'diamond' ? 'bold' : 'normal'
              }}>
              Diamond
            </span>
          </div>
        </div>


        <div
          style={{
            color: theme.text,
            textAlign: 'center',
            width: '100%',
            fontSize: '20px',
          }}
        >
          {/* {viewBy === 'crop' ? 'Cropplan Timeline' : 'Strain Timeline'} */}
          {viewBy === 'crop'
            ? chartView === 'chart'
              ? 'Cropplan Timeline'
              : 'Cropplan Diamond View'
            : chartView === 'chart'
              ? 'Strain Timeline'
              : 'Strain Diamond View'}

        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span>Time Range :</span>
          <div style={{ display: 'flex', gap: '4px' }}>

            {range.map((range) => (
              <Button
                key={range}
                onClick={() => setTimeRange(range)}
                style={{
                  backgroundColor: timeRange === range ? '#B1D23A' : '#444',
                  border: '1px solid #666',
                  borderRadius: '4px',
                  padding: '8px 16px',
                  height: "32px",
                  fontSize: '12px',
                  cursor: 'pointer',
                  fontWeight: timeRange === range ? 'bold' : 'normal',
                  color: timeRange === range ? 'black' : 'white',
                  transition: 'all 0.2s'

                }}
              >{range}
              </Button>
            ))}


          </div>
        </div>
      </div>

      {/* Fixed Timeline Header - NO SCROLLBAR */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        backgroundColor: theme.bg,
        borderBottom: '1px solid #444',
      }}>
        <div
          ref={headerContainerRef}
          style={{
            overflowX: 'hidden', // CHANGED: Hide horizontal scrollbar on header
            overflowY: 'hidden',
          }}
          className={`customScroll ${currDarkThemeClass}`}
        >
          <svg
            ref={headerSvgRef}
            width={timelineWidthData.timelineWidth}
            height={100}
            style={{ backgroundColor: 'transparent', display: 'block' }}
          />
        </div>
      </div>

      {/* Scrollable Content - Only this has the scrollbar */}
      <div
        ref={containerRef}
        className={`customScroll ${currDarkThemeClass}`}
        style={{
          flex: 1,
          overflowX: 'auto',
          overflowY: 'auto',
          position: 'relative'
        }}
      >
        <svg
          ref={svgRef}
          width={timelineWidthData.timelineWidth}
          height={dynamicHeight}
          style={{ backgroundColor: 'transparent', display: 'block' }}
        />
      </div>
      <IconButton
          onClick={handleOpen}
          sx={{
            position: 'sticky',
            bottom: 56,
            right: 16,
            marginLeft: 'auto',
            display: 'block',
            zIndex: 10,
            backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'grey',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            width: '40px',
            height: '40px',
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24">
            <path
              d="M3 5H21L14 13V18L10 20V13L3 5Z"
              stroke="white"
              strokeWidth="1.8"
            />
          </svg>
        </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            backgroundColor: theme.bg,
            color: theme.text,
            borderRadius: '16px',
            padding: '12px',
            minWidth: '220px',
            boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
          }
        }}
      >
        {/* Title */}
        <Typography sx={{ fontWeight: 600, mb: 1 }}>
          Crop run state
        </Typography>

        {/* Select All */}
        <FormControlLabel
          control={
            <Checkbox
              checked={tempStates.length === Object.keys(statusChoices || {}).length}
              onChange={(e) =>
                e.target.checked ? handleSelectAll() : handleClearAll()
              }
            />
          }
          label="All States"
        />

        <Box sx={{ borderBottom: '1px solid #444', mb: 1 }} />

         {/* Status list */}
         <Box sx={{ display: 'flex', flexDirection: 'column' }}>
           {(Object.keys(statusChoices || {})).map((status) => (
             <FormControlLabel
               key={status}
               control={
                 <Checkbox
                   checked={tempStates.includes(status)}
                   onChange={(e) => handleTempChange(status, e.target.checked)}
                 />
               }
               label={status}
             />
           ))}
         </Box>

         {/* Apply */}
         <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
           <Button size="small" onClick={handleApply}>
             Apply
           </Button>
         </Box>
       </Popover>
    </div>
    {selectedRoutine && (
      <RoutinesPopup
        open={Boolean(selectedRoutine)}
        onClose={() => {
          setSelectedRoutine(null);
          setExpandedSubphaseId(null);
        }}
        routine={selectedRoutine}
        assignees={assignees}
        users={users}
        subphase={selectedRoutine?.subphase}
        cropplanid={selectedRoutine?.cropplan_id}
        currDarkThemeClass={currDarkThemeClass}
        cropPlanPerms={cropPlanPerms}


        onSaveClick={handleRoutineSave(
          selectedRoutine?.subphase?.id,
          selectedRoutine?.id
        )}
      />
    )}
    

    {activeSubphase && (
      <Box
        sx={{
          position: 'absolute',
          right: '20px',
          top: '100px',
          width: '360px',
          height: '75vh',
          background: theme.panelBg,
          color: theme.text,
          borderRadius: '16px',
          boxShadow: '0 0 40px rgba(0,0,0,0.7)',
          padding: '20px',
          zIndex: 10,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '0px',
        }}
      >
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
          <Box display="flex" alignItems="center" gap="10px">
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: '#B1D23A',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#000',
                fontWeight: 'bold',
                fontSize: '14px',
                flexShrink: 0,
              }}
            >
              ✓
            </Box>
            <Typography fontWeight="700" fontSize="15px" lineHeight={1.3}>
              {activeSubphase.phase?.name}
            </Typography>
          </Box>
          <Typography
            sx={{ cursor: 'pointer', fontSize: '18px', color: theme.textMuted, lineHeight: 1, ml: 1 }}
            onClick={() => { setActiveSubphase(null); setExpandedSubphaseId(null); }}
          >
            ✕
          </Typography>
        </Box>

        {/* Breadcrumb */}
        <Typography sx={{ color: theme.textMuted, fontSize: '12px', mb: 2 }}>
          {activeSubphase.phase?.name} / {activeSubphase.subphase?.name}
        </Typography>

        <Box sx={divider} />

        {/* Moved from / to */}
        <Box display="flex" justifyContent="space-between" mt={2} mb={2}>
          <Box>
            <Typography sx={{ color: theme.textMuted, fontSize: '11px', mb: 0.5 }}>Moved from</Typography>
            <Typography fontSize="14px">--</Typography>
          </Box>
          <Box textAlign="right">
            <Typography sx={{ color: theme.textMuted, fontSize: '11px', mb: 0.5 }}>Moved to</Typography>
            <Typography fontSize="14px">--</Typography>
          </Box>
        </Box>

        <Box sx={divider} />

        {/* Started / Ended on (days) */}
        <Box display="flex" justifyContent="space-between" mt={2} mb={2}>
          <Box>
            <Typography sx={{ color: theme.textMuted, fontSize: '11px', mb: 0.5 }}>Started on</Typography>
            <Typography fontSize="14px">{activeSubphase.subphase?.start_day}</Typography>
          </Box>
          <Box textAlign="right">
            <Typography sx={{ color: theme.textMuted, fontSize: '11px', mb: 0.5 }}>Ended on</Typography>
            <Typography fontSize="14px">{activeSubphase.subphase?.end_day}</Typography>
          </Box>
        </Box>

        <Box sx={divider} />

        {/* Start / End dates */}
        <Box display="flex" justifyContent="space-between" mt={2} mb={2}>
          <Box>
            <Typography sx={{ color: theme.textMuted, fontSize: '11px', mb: 0.5 }}>Start Date</Typography>
            <Typography fontSize="14px">
              {activeSubphase.subphase?.start_date?.split('T')[0] ?? '--'}
            </Typography>
          </Box>
          <Box textAlign="right">
            <Typography sx={{ color: theme.textMuted, fontSize: '11px', mb: 0.5 }}>End Date</Typography>
            <Typography fontSize="14px">
              {activeSubphase.subphase?.end_date?.split('T')[0] ?? '--'}
            </Typography>
          </Box>
        </Box>

        <Box sx={divider} />

        {/* Description */}
        <Box mt={2} sx={{ background: theme.innerCardBg, padding: '14px', borderRadius: '10px' }}>
          <Typography fontWeight="600" fontSize="13px" mb={0.75}>
            Sub-phase description
          </Typography>
          <Typography sx={{ fontSize: '13px', color: theme.textMuted, lineHeight: 1.6 }}>
            {activeSubphase.subphase?.name} phase details...
          </Typography>
        </Box>

        {/* Routines */}
        <Box mt={2}>
          <Typography fontWeight="600" fontSize="13px" mb={1}>
            Routines
          </Typography>
          {activeSubphase.subphase?.routines?.length > 0 ? (
            activeSubphase.subphase.routines.map((routine, i) => (
              <Box
                key={i}
                onClick={(e) => {
                  e.stopPropagation();

                  console.log("ROUTINE CLICKED:", routine);

                  APIService.fetchInstance("new/cropplan/")
                    .then(res => {
                      setAssignees(res.data.assignees);
                      setUsers(res.data.users);
                    });

                  setSelectedRoutine({
                    ...routine,
                    subphase: activeSubphase.subphase
                  });
                }}
                sx={{
                  mb: 1.5,
                  padding: '12px 14px',
                  borderRadius: '10px',
                  background: theme.cardBg,
                  border: `1px solid ${theme.border}`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  '&:hover': { borderColor: '#B1D23A', background: theme.cardBg },
                  transition: 'all 0.15s ease',
                }}
              >
                <Box>
                  <Typography fontSize="13px" fontWeight="500" mb={0.4}>
                    {routine.name}
                  </Typography>
                  <Typography sx={{ fontSize: '11px', color: theme.textMuted }}>
                    Total tasks {routine.tasks_count ?? 0} &nbsp;•&nbsp; Complete{' '}
                    {routine.routine_tasks?.filter(t => t.task_status === 'complete').length ?? 0}
                  </Typography>
                </Box>
                <Typography sx={{ color: '#555', fontSize: '16px', ml: 1 }}>›</Typography>
              </Box>
            ))
          ) : (
            <Typography sx={{ fontSize: '12px', color: '#555' }}>No routines assigned.</Typography>
          )}
        </Box>
      </Box>
    )}
    </>
  );
};

export default CommandCenterChart;